import { ApiError, type HttpMethod, requestJson } from "@unizpay/ui";

export type AdminRole = "owner" | "admin" | "viewer";

export const ROLE_LABEL: Record<AdminRole, string> = { owner: "Owner", admin: "Admin", viewer: "Viewer" };

/** What each role may do, as shown on the account page. */
export const ROLE_DESCRIPTION: Record<AdminRole, string> = {
  owner: "Full access, including managing other admins.",
  admin: "Day-to-day operations.",
  viewer: "Read only.",
};

export interface Me {
  id: string;
  email: string;
  name: string | null;
  role: AdminRole;
  last_login_at: string | null;
  created_at: string;
}

export interface RelayerBalance {
  chain_id: string;
  name: string;
  family: "evm" | "solana" | "tron";
  testnet: boolean;
  enabled: boolean;
  symbol: string;
  address: string | null;
  explorer_url: string | null;
  balance: string | null;
  usd: string | null;
  low: boolean;
  resources?: { energy: string; bandwidth: string };
  error: string | null;
}

export interface RelayerBalances {
  networks: RelayerBalance[];
  checked_at: string;
}

let onUnauthorized: (() => void) | null = null;

/** Called whenever an admin API request returns 401 (signed out or the session expired). */
export function setUnauthorizedHandler(fn: () => void) {
  onUnauthorized = fn;
}

function request<T>(method: HttpMethod, path: string, body?: unknown): Promise<T> {
  return requestJson<T>(method, `/admin-api${path}`, {
    body,
    // A wrong password at sign-in is a 401 too: that is not a lost session.
    onUnauthorized: path === "/auth/login" ? undefined : () => onUnauthorized?.(),
  });
}

export const api = {
  login: (email: string, password: string) =>
    request<{ ok: true }>("POST", "/auth/login", { email, password }),
  logout: () => request<{ ok: true }>("POST", "/auth/logout", {}),
  me: () => request<Me>("GET", "/me"),
  updateMe: (input: { name: string | null }) => request<Me>("PATCH", "/me", input),
  changePassword: (current_password: string, new_password: string) =>
    request<{ ok: true; other_sessions_signed_out: number }>("POST", "/me/password", {
      current_password,
      new_password,
    }),
  relayers: () => request<RelayerBalances>("GET", "/relayers"),
};

export { ApiError };
